"use client";

import { signIn } from "next-auth/react";
import { useSession } from "next-auth/react";
import { useNavigation } from "@/context/NavigationContext";
import { useEffect, useState, useMemo } from "react";
import { Consultation, Scenario } from "@/app/lib/types";
import Loading from "./loading";
import { getScenarioEmoji } from "./utils/getScenarioEmoji";
import { Joyride, Step } from "react-joyride";
import DashboardCard from "./components/DashboardCard";
import DashboardListItem from "./components/DashboardListItem";

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return { text: "Good morning", emoji: "🌅" };
  if (hour < 18) return { text: "Good afternoon", emoji: "🌞" };
  return { text: "Good evening", emoji: "🌙" };
}

const tourSteps: Step[] = [
  {
    target: "#latest-scenarios",
    content:
      "Here you can find the latest scenarios that have been added to the app. Click on a scenario to start a new consultation.",
    skipBeacon: true,
  },
  {
    target: "#see-all-scenarios",
    content:
      "Click here to view all available scenarios. You can also create new scenarios or duplicate existing ones for your own practice.",
    skipBeacon: true,
  },
  {
    target: "#recent-consultations",
    content:
      "This section shows your recent consultations. If you've just started, there won't be any here yet.",
    skipBeacon: true,
  },
  {
    target: "#see-all-consultations",
    content: "Click here to view all your consultations or to start a new one.",
    skipBeacon: true,
  },
];

const Home = () => {
  const { status } = useSession();
  const { handleNavigation } = useNavigation();
  const { data: session } = useSession();

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [loading, setLoading] = useState(true);
  const [runTour, setRunTour] = useState(false);

  const greeting = useMemo(getGreeting, []);

  useEffect(() => {
    if (status === "authenticated") {
      setLoading(true);
      Promise.all([
        fetch("/api/scenarios").then((res) => res.json()),
        fetch("/api/consultations").then((res) => res.json()),
      ]).then(([scenariosData, consultationsData]) => {
        scenariosData.sort(
          (a: Scenario, b: Scenario) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        consultationsData.sort(
          (a: Consultation, b: Consultation) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        setScenarios(scenariosData);
        setConsultations(consultationsData);
        setLoading(false);
      });
    }
  }, [status]);

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        locale={{ last: "Close" }}
        options={{
          zIndex: 10000,
          buttons: ["back", "skip", "primary"],
          showProgress: true,
        }}
        styles={{
          buttonBack: {
            color: "black",
            border: "1px solid black",
            backgroundColor: "transparent",
            borderRadius: "4px",
            padding: "4px 12px",
            lineHeight: "1.4",
          },
        }}
      />
      {status === "authenticated" ? (
        loading ? (
          <Loading />
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center justify-center gap-2 mb-6">
              <h1 className="text-3xl font-semibold text-center m-0">
                {greeting.emoji} {greeting.text},{" "}
                {session?.user?.name || "User"}
              </h1>
              <button
                className="btn btn-info mt-3"
                onClick={() => {
                  setRunTour(false);
                  setTimeout(() => setRunTour(true), 0);
                }}
                type="button"
                aria-label="Start Tour"
              >
                Tour this page
              </button>
            </div>
            <div className="w-full max-w-3xl p-8 flex flex-col sm:flex-row gap-8 justify-center">
              <DashboardCard
                id="latest-scenarios"
                title="Latest Scenarios"
                countLabel={`Scenario${scenarios.length === 1 ? "" : "s"}`}
                count={scenarios.length}
                onSeeAll={() => handleNavigation("/scenarios")}
                seeAllProps={{ id: "see-all-scenarios" }}
              >
                {scenarios.length ? (
                  scenarios
                    .slice(0, 4)
                    .map((scenario) => (
                      <DashboardListItem
                        key={scenario.id}
                        href={`/consultations/new?scenarioId=${scenario.id}`}
                        title={scenario.title}
                        leftContent={getScenarioEmoji(scenario)}
                        rightLabel="New Consultation"
                      />
                    ))
                ) : (
                  <li>No scenarios found.</li>
                )}
              </DashboardCard>
              <DashboardCard
                id="recent-consultations"
                title="Your Recent Consultations"
                countLabel={`Consultation${consultations.length === 1 ? "" : "s"}`}
                count={consultations.length}
                onSeeAll={() => handleNavigation("/consultations")}
                seeAllProps={{ id: "see-all-consultations" }}
              >
                {consultations.length ? (
                  consultations
                    .slice(0, 4)
                    .map((exam) => (
                      <DashboardListItem
                        key={exam.id}
                        href={`/consultations/${exam.id}`}
                        title={exam.title}
                        rightLabel="Continue"
                      />
                    ))
                ) : (
                  <li>No consultations found.</li>
                )}
              </DashboardCard>
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-col items-center justify-center mt-20 px-4">
          <h1 className="text-5xl font-extrabold mb-4 text-center text-primary drop-shadow">
            CaseConnect AI
          </h1>
          <p className="mb-6 text-xl text-center max-w-2xl text-base-content/75">
            CaseConnect AI enables medical and veterinary students practice to clinical skills through interactive scenarios.
          </p>
          {/* Feature Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8 w-full max-w-4xl">
            <div className="bg-base-100 rounded-xl shadow-md flex flex-col items-center p-6">
              <span className="text-4xl mb-2">🩺</span>
              <span className="text-lg font-semibold mb-1">Scenarios</span>
              <span className="text-sm text-base-content/75 text-center">
                Choose from a set of instructor created scenarios or create your own.
              </span>
            </div>
            <div className="bg-base-100 rounded-xl shadow-md flex flex-col items-center p-6">
              <span className="text-4xl mb-2">💬</span>
              <span className="text-lg font-semibold mb-1">Consultations</span>
              <span className="text-sm text-base-content/75 text-center">
                Practice interactions with an AI patient to develop your consultation skills.
              </span>
            </div>
            <div className="bg-base-100 rounded-xl shadow-md flex flex-col items-center p-6">
              <span className="text-4xl mb-2">
                📝
              </span>
              <span className="text-lg font-semibold mb-1">Feedback</span>
              <span className="text-sm text-base-content/75 text-center">
                Receive AI-generated feedback on your performance to help you improve.
              </span>
            </div>
          </div>
          {/* Call to Action */}
          <button
            className="btn btn-primary btn-lg shadow-lg transition-transform hover:scale-105"
            aria-label="Sign in with Azure AD"
            onClick={() =>
              signIn("azure-ad", { callbackUrl: "/" }, { prompt: "login" })
            }
          >
            Sign in with uun@ed.ac.uk
          </button>
          <a className="link mt-8 text-sm font-semibold" href="/your-data" target="_blank">
            Your Data
          </a>
        </div>
      )}
    </>
  );
};

export default Home;
